import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import global from 'common'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'

import { ProductIds } from 'core'
import { CoverageProviderResponse, getCoverageProviders } from 'core/application/expenses/getCoverageProviders'

import Option from 'common/components/dataEntry/components/Option'
import { Input, Select } from 'common/components/dataEntry/formik'
import Modal from 'common/components/feedback/Modal'
import Button from 'common/components/general/Button'

import useQueryRequest from 'common/hooks/useQueryRequest'

import { parseZoneMonthYearFormat } from 'common/dateFormatters'
import { useLoginUserV2 } from 'common/hooks'

import './styles.less'

export const ADD_COVERAGE_PROVIDER: string = 'add'

type ExpenseCoverageProviderSelectProps = {
  name: string
  defaultValue?: any
  value?: any
  date: string
  familyMemberId?: string
}

const ExpenseCoverageProviderSelect: React.FC<ExpenseCoverageProviderSelectProps> = ({
  defaultValue,
  date,
  value,
  familyMemberId,
  name,
}) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const [coverageProviders, , sendGetCoverageProviders] = useQueryRequest<CoverageProviderResponse[]>([])

  const [showModal, setShowModal] = useState(false)
  const [isFormValid, setIsFormValid] = useState<boolean>(false)

  const {
    user: { productId },
  } = useLoginUserV2()

  const isQsehra = productId === ProductIds.QsehraId

  const context = useFormikContext()

  const onChange = (value: any): void => {
    if (ADD_COVERAGE_PROVIDER === value) {
      setShowModal(true)
    }
  }

  useEffect(() => {
    ;(async () => {
      await sendGetCoverageProviders(() => getCoverageProviders(date, familyMemberId, isQsehra))
    })()
  }, [date, familyMemberId])

  const handleSubmit = async (values: any) => {
    context.setFieldValue(name, values.name)
    setShowModal(false)
  }

  return (
    <>
      <Select
        label={'Coverage Provider'}
        name={name}
        placeholder={'Select a coverage provider'}
        showRequiredSign={true}
        onChange={onChange}
        value={value ? [value] : null}
      >
        {coverageProviders.map((item: CoverageProviderResponse) => (
          <Option key={item.id} value={item.name}>
            {(isQsehra && `${item.name}`) || `${item.name}(${parseZoneMonthYearFormat(item.providerStartDate)})`}
          </Option>
        ))}
        <Option key={ADD_COVERAGE_PROVIDER} value={ADD_COVERAGE_PROVIDER}>
          <global.components.general.Icon type="fa fa-plus" fontAwesome />
          {`Add coverage provider`}
        </Option>
      </Select>

      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={{ name: '' }}
        onSubmit={handleSubmit}
        validate={(values) => {
          setIsFormValid(formValidator(translate).isValidSync(values))
        }}
      >
        {(props) => {
          return (
            <form autoComplete="off">
              <Modal
                title={'Add Coverage Provider'}
                visible={showModal}
                destroyOnClose
                closable
                onCancel={() => {
                  setShowModal(false)
                  context.setFieldValue(name, defaultValue)
                }}
                footer={[
                  <Button
                    key="back"
                    onClick={() => {
                      setShowModal(false)
                      context.setFieldValue(name, defaultValue)
                    }}
                  >
                    {translate('translation:general.cancel')}
                  </Button>,
                  <Button key="submit" type="primary" disabled={!isFormValid} onClick={() => props.handleSubmit()}>
                    {translate('translation:general.add')}
                  </Button>,
                ]}
              >
                <div className={'mb-16'}>
                  <Input showRequiredSign={true} label={'Provider Name'} name="name" placeholder={'Provider name'} />
                </div>
              </Modal>
            </form>
          )
        }}
      </Formik>
    </>
  )
}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    name: Yup.string().nullable().required(translate('translation:general.required')),
  })

export default ExpenseCoverageProviderSelect
